import { z } from 'zod';

import {
  tareProfileModeValues,
  tareProfileValidationIssueCodeValues,
  type CreateTareProfileRequest,
  type CreateTareProfileResponse,
  type GetTareProfileResponse,
  type ListTareProfilesByLocationRequest,
  type ListTareProfilesByLocationResponse,
  type TareProfileDto,
  type TareProfileSummaryDto,
  type TareProfileValidationIssue,
  type TareProfileValidationResult,
  type UpdateTareProfileRequest,
  type UpdateTareProfileResponse,
} from './tare-profiles.dto.js';

const timestampSchema = z.iso.datetime();
const MAX_TARE_LB = 50000;
const codeSchema = z.string().trim().min(1).max(64);
const displayNameSchema = z.string().trim().min(1).max(120);
const descriptionSchema = z.string().trim().min(1).max(500).nullable();
const defaultTareLbSchema = z.number().min(0).max(MAX_TARE_LB);

export const TareProfileModeSchema = z.enum(tareProfileModeValues);
export const TareProfileValidationIssueCodeSchema = z.enum(
  tareProfileValidationIssueCodeValues,
);

function addTareModeIssues(
  value: { mode: (typeof tareProfileModeValues)[number]; defaultTareLb?: number | null },
  ctx: z.RefinementCtx,
): void {
  if (value.mode === 'fixed_default') {
    if (value.defaultTareLb == null) {
      ctx.addIssue({
        code: 'custom',
        message: 'fixed_default tare profiles require defaultTareLb.',
        path: ['defaultTareLb'],
      });
    }
    return;
  }

  if (value.defaultTareLb != null) {
    ctx.addIssue({
      code: 'custom',
      message: `${value.mode} tare profiles cannot store defaultTareLb.`,
      path: ['defaultTareLb'],
    });
  }
}

export const TareProfileValidationIssueSchema: z.ZodType<TareProfileValidationIssue> =
  z.object({
    code: TareProfileValidationIssueCodeSchema,
    field: z.string().trim().min(1).optional(),
    message: z.string().trim().min(1),
  });

export const TareProfileValidationResultSchema: z.ZodType<TareProfileValidationResult> =
  z.object({
    isValid: z.boolean(),
    issues: z.array(TareProfileValidationIssueSchema),
  });

export const TareProfileDtoSchema: z.ZodType<TareProfileDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    code: codeSchema,
    displayName: displayNameSchema,
    description: descriptionSchema.optional(),
    mode: TareProfileModeSchema,
    defaultTareLb: defaultTareLbSchema.nullable().optional(),
    allowOperatorOverride: z.boolean(),
    requireOperatorConfirmation: z.boolean(),
    isActive: z.boolean(),
    displayOrder: z.number().int().min(0),
    revision: z.number().int().min(1),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .superRefine(addTareModeIssues);

export const TareProfileSummaryDtoSchema: z.ZodType<TareProfileSummaryDto> =
  z
    .object({
      id: z.uuid(),
      code: codeSchema,
      displayName: displayNameSchema,
      description: descriptionSchema.optional(),
      mode: TareProfileModeSchema,
      defaultTareLb: defaultTareLbSchema.nullable().optional(),
      allowOperatorOverride: z.boolean(),
      requireOperatorConfirmation: z.boolean(),
      revision: z.number().int().min(1),
    })
    .superRefine(addTareModeIssues);

export const ListTareProfilesByLocationRequestSchema: z.ZodType<ListTareProfilesByLocationRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      includeInactive: z.boolean().optional(),
    })
    .strict();

export const ListTareProfilesByLocationResponseSchema: z.ZodType<ListTareProfilesByLocationResponse> =
  z.object({
    items: z.array(TareProfileDtoSchema),
  });

export const CreateTareProfileRequestSchema: z.ZodType<CreateTareProfileRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      code: codeSchema,
      displayName: displayNameSchema,
      description: descriptionSchema.optional(),
      mode: TareProfileModeSchema,
      defaultTareLb: defaultTareLbSchema.nullable().optional(),
      allowOperatorOverride: z.boolean().optional(),
      requireOperatorConfirmation: z.boolean().optional(),
      isActive: z.boolean().optional(),
      displayOrder: z.number().int().min(0).optional(),
    })
    .strict()
    .superRefine(addTareModeIssues);

export const CreateTareProfileResponseSchema: z.ZodType<CreateTareProfileResponse> =
  z.object({
    item: TareProfileDtoSchema,
  });

export const UpdateTareProfileRequestSchema: z.ZodType<UpdateTareProfileRequest> =
  z
    .object({
      companyId: z.uuid(),
      tareProfileId: z.uuid(),
      displayName: displayNameSchema.optional(),
      description: descriptionSchema.optional(),
      mode: TareProfileModeSchema.optional(),
      defaultTareLb: defaultTareLbSchema.nullable().optional(),
      allowOperatorOverride: z.boolean().optional(),
      requireOperatorConfirmation: z.boolean().optional(),
      isActive: z.boolean().optional(),
      displayOrder: z.number().int().min(0).optional(),
    })
    .strict()
    .refine(
      (value) =>
        value.displayName !== undefined
        || value.description !== undefined
        || value.mode !== undefined
        || value.defaultTareLb !== undefined
        || value.allowOperatorOverride !== undefined
        || value.requireOperatorConfirmation !== undefined
        || value.isActive !== undefined
        || value.displayOrder !== undefined,
      { message: 'At least one field must be provided to update tare profile' },
    );

export const UpdateTareProfileResponseSchema: z.ZodType<UpdateTareProfileResponse> =
  z.object({
    item: TareProfileDtoSchema,
  });

export const GetTareProfileResponseSchema: z.ZodType<GetTareProfileResponse> =
  z.object({
    item: TareProfileDtoSchema,
  });

export const tareProfileSchemaLimits = {
  maxTareLb: MAX_TARE_LB,
  maxCodeLength: 64,
  maxDisplayNameLength: 120,
  maxDescriptionLength: 500,
} as const;
